//pages/QuoteView/LoadBuilding/TaiSectionForms/UnifiedTaiLoadForm.tsx
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  FormProvider,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import {
  BoxIcon,
  Building2,
  CircleDollarSignIcon,
  ReceiptIcon,
  Truck,
  WarehouseIcon,
  Weight,
} from 'lucide-react';

import { Accordion } from 'components/Accordion';
import { Button } from 'components/Button';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { useToast } from 'hooks/useToaster';
import { getCustomers } from 'lib/api/getCustomers';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { LoadSectionAccordionItem } from 'pages/LoadView/LoadInformation/Components';
import { CustomerSectionForm as LoadViewCustomerSectionForm } from 'pages/LoadView/LoadInformation/Sections/CustomerSectionForm';
import { SpecificationsForm as LoadViewSpecificationsForm } from 'pages/LoadView/LoadInformation/Sections/SpecificationsForm';
import LoadBuildingAccordionControls from 'pages/QuoteView/LoadBuilding/components/LoadBuildingAccordionControls';
import { LoadCreatedMessage } from 'pages/QuoteView/LoadBuilding/components/LoadCreatedMessage';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { suggestedTransportTypeOrEmpty } from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import {
  CostLineItem,
  Dropoff,
  Load,
  LoadStopTypes,
  NormalizedLoad,
  Pickup,
  TMSCustomer,
  Unit,
  createInitLoad,
  createInitRateData,
  createInitSpecs,
  normalizeLoad,
} from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMSOperation } from 'types/api/UpdateTMS';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  injectSelectedObject,
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';
import {
  denormalizeDatesForTMSForm,
  normalizeDateForTMS,
} from 'utils/parseDatesForTMSForm';

import {
  validateDeliveryOnOrAfterPickupDate,
  validateDuplicateStopLocations,
} from '../utils/stopValidation';
import AdditionalReferencesForm from './AdditionalReferences';
import { CarrierSectionForm } from './Carrier';
import { CustomerSectionForm } from './Customer';
import { RatesForm } from './Rates';
import { SpecificationsForm } from './Specifications';
import { StopForm } from './Stop';
import {
  matchTaiCatalogTransportType,
  normalizeTaiCarrierRateData,
  normalizeTaiLoadForForm,
  toTaiFormTransportType,
} from './utils';

enum AvailableTabs {
  customer = 'customer',
  carrier = 'carrier',
  specifications = 'specifications',
  rates = 'rates',
  pickup = 'pickup',
  consignee = 'consignee',
  additionalReferences = 'additionalReferences',
}

const initLoadBuildingForm = (tmsName: string): NormalizedLoad =>
  normalizeLoad(tmsName, {
    ...createInitLoad(),
    mode: 'Truckload',
    specifications: {
      ...createInitSpecs(),
      transportType: 'Van',
      serviceType: 'Any',
    },
    rateData: {
      ...createInitRateData(),
      customerTotalCharge: {
        val: 0,
        unit: Unit.USD,
      },
    },
  });

interface UnifiedTaiLoadFormProps {
  isCreateMode: boolean;
  load?: Load;
  onSubmit?: (data: NormalizedLoad) => void;
  onInvalid?: SubmitErrorHandler<NormalizedLoad>;
  onSuccess?: (result: any) => void;
  onError?: (error: any) => void;
}

export default function UnifiedTaiLoadForm({
  isCreateMode,
  load,
  onSubmit,
  onInvalid,
  onSuccess,
  onError,
}: UnifiedTaiLoadFormProps) {
  const { toast } = useToast();
  const guardedCreateLoad = useGuardedCreateLoad();
  const { tmsIntegrations } = useServiceFeatures();
  const tmsName = TMS.Tai;
  const tmsID = isCreateMode ? tmsIntegrations?.[0]?.id : load?.tmsID;

  const [isLoading, setIsLoading] = useState(false);
  // additionalReferences is edit-only.
  const allTabs = useMemo(() => {
    const tabs = [
      AvailableTabs.customer,
      AvailableTabs.carrier,
      AvailableTabs.specifications,
      AvailableTabs.rates,
      AvailableTabs.pickup,
      AvailableTabs.consignee,
    ];
    if (!isCreateMode) {
      tabs.push(AvailableTabs.additionalReferences);
    }
    return tabs;
  }, [isCreateMode]);
  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs });
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);

  const [isLoadingCustomers, setIsLoadingCustomers] = useState(true);
  const [fetchedCustomers, setFetchedCustomers] =
    useState<Maybe<TMSCustomer[]>>(null);

  const [isLoadBuildingSuggestionClicked, setIsLoadBuildingSuggestionClicked] =
    useState(false);

  const {
    currentState: {
      clickedSuggestion,
      threadId,
      emailId,
      threadItemId,
      goToSuggestion,
    },
    setCurrentState,
  } = useContext(SidebarStateContext);

  const fetchCustomers = async () => {
    if (!tmsID) return;
    setIsLoadingCustomers(true);
    const res = await getCustomers(tmsID);
    if (res.isOk()) {
      setFetchedCustomers(res.value.customerList);
    } else {
      const isWarning = res.error.level === 'warning';
      toast({
        description: res.error.message || 'Error while fetching customer list.',
        variant: isWarning ? 'warning' : 'destructive',
      });
    }
    setIsLoadingCustomers(false);
  };

  // Latest-ref fetcher: this effect must run only on tmsID change (Tai uses
  // quick-entry stops only; no TMS locations fetch).
  const fetchCustomersRef = useRef(fetchCustomers);
  fetchCustomersRef.current = fetchCustomers;

  useEffect(() => {
    void fetchCustomersRef.current();
  }, [tmsID]);

  useEffect(() => {
    // Only auto-navigate to load-building suggestions in create mode
    if (isCreateMode && !clickedSuggestion) {
      goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
  }, [isCreateMode, clickedSuggestion, goToSuggestion]);

  useEffect(() => {
    if (
      isCreateMode &&
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      setIsLoadBuildingSuggestionClicked(true);
    } else {
      setIsLoadBuildingSuggestionClicked(false);
    }
  }, [isCreateMode, clickedSuggestion]);

  const createModeSuggestionId = isCreateMode
    ? (clickedSuggestion?.id ?? null)
    : null;

  // Read the suggestion payload through a ref so the defaults below are only
  // re-derived when the create-mode suggestion id changes, never on the
  // transient clickedSuggestion identity churn from extension messages.
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const memoizedDefaultValues: NormalizedLoad = useMemo(() => {
    // Edit mode
    if (!isCreateMode && load) {
      const normalized = normalizeTaiLoadForForm(normalizeLoad(tmsName, load));
      return {
        ...normalized,
        mode: 'Truckload', // Always TL for initial implementation
        specifications: {
          ...normalized.specifications,
          transportType: toTaiFormTransportType(
            normalized.specifications?.transportType
          ),
        },
      };
    }

    if (isCreateMode && builtLoad) {
      const normalized = normalizeTaiLoadForForm(
        normalizeLoad(tmsName, builtLoad)
      );
      return {
        ...normalized,
        mode: 'Truckload',
        specifications: {
          ...normalized.specifications,
          transportType: toTaiFormTransportType(
            normalized.specifications?.transportType
          ),
        },
      };
    }

    // Create mode (with suggestions)
    const activeSuggestion =
      createModeSuggestionId == null ? null : clickedSuggestionRef.current;
    if (
      isCreateMode &&
      activeSuggestion &&
      activeSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      const casted = activeSuggestion.suggested as SuggestedLoad;
      const suggestedTransportType = matchTaiCatalogTransportType(
        suggestedTransportTypeOrEmpty(casted.specifications)
      );

      const customerLineItemsArray: CostLineItem[] =
        normalizeToArray(casted.rateData?.customerLineItems) || [];
      const carrierLineItemsArray: CostLineItem[] =
        normalizeToArray(casted.rateData?.carrierLineItems) || [];

      const suggestedFields: Partial<NormalizedLoad> = {
        ...casted,
        mode: 'Truckload',
        // Preserve refNumberCandidates fields
        customer: {
          ...casted.customer,
          refNumberCandidates: normalizeToStringArray(
            casted.customer?.refNumberCandidates
          ),
        },
        pickup: {
          ...casted.pickup,
          readyTime: casted.pickup?.readyTime
            ? normalizeDateForTMS(casted.pickup.readyTime, tmsName)
            : null,
          apptStartTime: casted.pickup?.apptStartTime
            ? normalizeDateForTMS(casted.pickup.apptStartTime, tmsName)
            : null,
          apptEndTime: casted.pickup?.apptEndTime
            ? normalizeDateForTMS(casted.pickup.apptEndTime, tmsName)
            : null,
          refNumberCandidates: normalizeToStringArray(
            casted.pickup?.refNumberCandidates
          ),
        },
        consignee: {
          ...casted.consignee,
          mustDeliver: casted.consignee?.mustDeliver
            ? normalizeDateForTMS(casted.consignee.mustDeliver, tmsName)
            : null,
          apptStartTime: casted.consignee?.apptStartTime
            ? normalizeDateForTMS(casted.consignee.apptStartTime, tmsName)
            : null,
          apptEndTime: casted.consignee?.apptEndTime
            ? normalizeDateForTMS(casted.consignee.apptEndTime, tmsName)
            : null,
          refNumberCandidates: normalizeToStringArray(
            casted.consignee?.refNumberCandidates
          ),
        },
        specifications: {
          ...createInitSpecs(),
          ...casted.specifications,
          transportType: suggestedTransportType,
        },
        rateData: {
          ...createInitRateData(),
          ...casted.rateData,
          customerTotalCharge: {
            ...casted.rateData?.customerTotalCharge,
            val: casted.rateData?.customerTotalCharge?.val ?? 0,
            unit: Unit.USD,
          },
          customerLineItems: customerLineItemsArray,
          carrierLineItems: carrierLineItemsArray,
        },
      };

      return {
        ...initLoadBuildingForm(tmsName),
        ...suggestedFields,
        mode: 'Truckload',
        rateData: {
          ...((suggestedFields.rateData as any) ?? {}),
          customerTotalCharge: {
            ...((suggestedFields.rateData as any)?.customerTotalCharge ?? {}),
            val:
              (suggestedFields.rateData as any)?.customerTotalCharge?.val ?? 0,
            unit: Unit.USD,
          },
        },
      } as NormalizedLoad;
    }

    // Create mode (no suggestion)
    return initLoadBuildingForm(tmsName);
  }, [isCreateMode, createModeSuggestionId, builtLoad, load, tmsName]);

  // Compute customers list that includes customer from load if it exists
  const customers = useMemo(() => {
    if (!memoizedDefaultValues?.customer?.externalTMSID)
      return fetchedCustomers;
    return injectSelectedObject(
      memoizedDefaultValues.customer as TMSCustomer,
      fetchedCustomers ?? []
    );
  }, [fetchedCustomers, memoizedDefaultValues?.customer]);

  const formMethods = useForm<NormalizedLoad>({
    defaultValues: memoizedDefaultValues,
  });

  // Create-mode-only Load Building suggestion id. Edit-mode Load View suggestion
  // clicks must not retrigger the reset below, or in-progress edits get wiped.
  const createModeLoadBuildingSuggestionId =
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
      ? clickedSuggestion.id
      : null;

  // Reset form when default values change
  useEffect(() => {
    formMethods.reset(memoizedDefaultValues, {
      keepDefaultValues: false,
      keepTouched: false,
      keepDirtyValues: false,
      keepDirty: false,
    });

    return scheduleLoadBuildingSuggestionValidation(
      createModeLoadBuildingSuggestionId != null && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );
  }, [
    memoizedDefaultValues,
    createModeLoadBuildingSuggestionId,
    builtLoad,
    formMethods,
  ]);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    load?.freightTrackingID,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    load?.freightTrackingID,
    setActiveTabs,
    !isCreateMode,
    Object.values(AvailableTabs)
  );

  // Persist form state on every change (only for create mode)
  useEffect(() => {
    if (!isCreateMode) return;
    const subscription = formMethods.watch((value) => {
      FormStorageService.saveFormState(`${tmsName}_${threadId}`, {
        threadID: threadId,
        values: value,
        clickedSuggestion: isLoadBuildingSuggestionClicked
          ? clickedSuggestion
          : null,
        dirtyFields: formMethods.formState.dirtyFields,
      });
    });
    return () => subscription.unsubscribe();
  }, [
    isCreateMode,
    formMethods,
    threadId,
    tmsName,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
  ]);

  function handleClearForm() {
    setBuiltLoad(null);
    FormStorageService.clearFormState(`${tmsName}_${threadId}`);

    // Deselect the Kit suggestion
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));

    formMethods.reset(initLoadBuildingForm(tmsName), {
      keepDefaultValues: false,
      keepDirty: false,
      keepDirtyValues: false,
      keepTouched: false,
    });
    formMethods.clearErrors();
  }

  const handleSubmit: SubmitHandler<NormalizedLoad> = async (data) => {
    setIsLoading(true);

    const applySubmitValidationErrors = (
      fieldErrors: ManualFieldError<NormalizedLoad>[]
    ) =>
      applyManualValidationErrors(
        formMethods,
        fieldErrors,
        loadBuildingManualValidationDisplayOptions({
          tmsName: TMS.Tai,
          activeTabs,
          setActiveTabs,
          toast,
          afterErrorHandled: () => setIsLoading(false),
        })
      );

    formMethods.clearErrors();

    const duplicateStops = validateDuplicateStopLocations(
      data.pickup?.externalTMSID,
      data.consignee?.externalTMSID
    );
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        { path: 'pickup.externalTMSID', message: duplicateStops.message },
        { path: 'consignee.externalTMSID', message: duplicateStops.message },
      ]);
      return;
    }

    const pickupDeliveryDates = validateDeliveryOnOrAfterPickupDate(
      data.pickup?.readyTime,
      data.consignee?.mustDeliver
    );
    if (!pickupDeliveryDates.ok) {
      applySubmitValidationErrors([
        { path: 'consignee.mustDeliver', message: pickupDeliveryDates.message },
      ]);
      return;
    }

    try {
      if (isCreateMode) {
        // Create new load
        data.freightTrackingID = '';
        data.externalTMSID = '';

        const specifications = data.specifications
          ? {
              ...data.specifications,
              transportSize: '', // Always send empty so backend defaults to "Full"
            }
          : data.specifications;

        const reqData = withLoadBuildingSuggestionContext(
          {
            clickedSuggestion,
            threadId,
            emailId,
            emailExternalId: threadItemId,
          },
          {
            load: {
              ...data,
              rateData: normalizeTaiCarrierRateData(data.rateData),
              specifications,
              tmsID: tmsID ?? 0,
              pickup: {
                ...denormalizeDatesForTMSForm(tmsName, data.pickup),
                apptNote: data.pickup?.apptNote ?? '',
                businessHours: data.pickup?.businessHours ?? '',
                refNumber: data.pickup?.refNumber ?? '',
              },
              consignee: {
                ...denormalizeDatesForTMSForm(tmsName, data.consignee),
                apptNote: data.consignee?.apptNote ?? '',
                businessHours: data.consignee?.businessHours ?? '',
                refNumber: data.consignee?.refNumber ?? '',
              },
            } as unknown as Load,
          }
        );

        const res = await guardedCreateLoad(data.customer, reqData, customers);
        if (!res) {
          setIsLoading(false);
          return;
        }
        if (res.isOk()) {
          setBuiltLoad(res.value.load);
          FormStorageService.clearFormState(`${tmsName}_${threadId}`);
          if (
            clickedSuggestion &&
            clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
          ) {
            setCurrentState((prevState) => ({
              ...prevState,
              ...applyCompleteKitSuggestions(
                prevState,
                clickedSuggestion.id,
                SuggestionPipelines.LoadBuilding
              ),
            }));
          }
          onSuccess?.(res.value);
        } else {
          const isWarning = res.error.level === 'warning';
          toast({
            description: res.error.message || 'Error building load.',
            variant: isWarning ? 'warning' : 'destructive',
          });
          onError?.(res.error);
        }
      } else {
        // Edit existing load
        if (!load?.ID) {
          toast({
            description: 'Load ID is required for updates.',
            variant: 'destructive',
          });
          setIsLoading(false);
          return;
        }

        // denormalizeDatesForTMSForm doesn't handle nested DateTime fields, so we need to
        // denormalize the pickup and consignee objects separately to avoid timezone issues
        const denormalizedData = {
          ...data,
          rateData: normalizeTaiCarrierRateData(data.rateData),
          pickup: denormalizeDatesForTMSForm(tmsName, data.pickup) as Pickup,
          consignee: denormalizeDatesForTMSForm(
            tmsName,
            data.consignee
          ) as Dropoff,
        };
        const res = await updateTMS(load.ID, {
          operation: TMSOperation.UpdateLoad,
          load: denormalizedData as Load,
        });

        if (res.isOk()) {
          toast({
            description: res.value.message || 'Load updated successfully!',
            variant: 'success',
          });
          onSuccess?.(res.value);
          await acceptLoadViewSuggestion(
            clickedSuggestion,
            denormalizedData as Load,
            setCurrentState,
            undefined,
            tmsName
          );
        } else {
          const isWarning = res.error.level === 'warning';
          toast({
            description: res.error.message || 'Error updating load.',
            variant: isWarning ? 'warning' : 'destructive',
          });
          onError?.(res.error);
        }
      }
    } catch (e) {
      toast({
        description: isCreateMode
          ? 'Error building load.'
          : 'Error updating load.',
        variant: 'destructive',
      });
      onError?.(e);
    } finally {
      setIsLoading(false);
      onSubmit?.(data);
    }
  };

  const handleInvalid: SubmitErrorHandler<NormalizedLoad> = async (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.Tai,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
    });

    if (onInvalid) {
      onInvalid(errors);
    }
  };

  const createModeContent = (
    <Accordion type='multiple' value={activeTabs} onValueChange={setActiveTabs}>
      <LoadSectionAccordionItem
        label='Customer'
        icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.customer}
        activeTabs={activeTabs}
      >
        <CustomerSectionForm
          formMethods={formMethods}
          customers={customers || []}
          setCustomers={setFetchedCustomers}
          isLoadingCustomers={isLoadingCustomers}
          setIsLoadingCustomers={setIsLoadingCustomers}
          originalSuggestionData={
            isCreateMode
              ? (clickedSuggestion?.suggested as NormalizedLoad)
              : undefined
          }
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Carrier'
        icon={<Truck className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.carrier}
        activeTabs={activeTabs}
      >
        <CarrierSectionForm
          formMethods={formMethods}
          tmsID={tmsID ?? null}
          isCreateMode={isCreateMode}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Specs'
        icon={<Weight className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.specifications}
        activeTabs={activeTabs}
      >
        <SpecificationsForm
          formMethods={formMethods}
          isCreateMode={isCreateMode}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Rates'
        icon={<CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.rates}
        activeTabs={activeTabs}
      >
        <RatesForm formMethods={formMethods} />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Pickup'
        icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.pickup}
        activeTabs={activeTabs}
      >
        <StopForm
          formMethods={formMethods}
          stop={LoadStopTypes.Pickup}
          originalSuggestionData={
            isCreateMode
              ? (clickedSuggestion?.suggested as NormalizedLoad)
              : undefined
          }
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Consignee'
        icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.consignee}
        activeTabs={activeTabs}
      >
        <StopForm
          formMethods={formMethods}
          stop={LoadStopTypes.Consignee}
          originalSuggestionData={
            isCreateMode
              ? (clickedSuggestion?.suggested as NormalizedLoad)
              : undefined
          }
        />
      </LoadSectionAccordionItem>
    </Accordion>
  );

  // Edit mode should match the classic Load View UI (Appointment Info, Address & Contact Info, etc.)
  // but still live inside unified form so Load Build + Load View share a single entrypoint.
  const editModeContent = (
    <Accordion type='multiple' value={activeTabs} onValueChange={setActiveTabs}>
      <LoadSectionAccordionItem
        label='Customer'
        icon={<Building2 className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.customer}
        activeTabs={activeTabs}
      >
        <LoadViewCustomerSectionForm
          customers={customers}
          setCustomers={setFetchedCustomers}
          isLoadingCustomers={isLoadingCustomers}
          setIsLoadingCustomers={setIsLoadingCustomers}
          formMethods={formMethods}
          originalSuggestionData={memoizedDefaultValues}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Carrier'
        icon={<Truck className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.carrier}
        activeTabs={activeTabs}
      >
        <CarrierSectionForm
          formMethods={formMethods}
          tmsID={tmsID ?? null}
          isCreateMode={isCreateMode}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Specs'
        icon={<Weight className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.specifications}
        activeTabs={activeTabs}
      >
        <LoadViewSpecificationsForm
          load={memoizedDefaultValues as any}
          formMethods={formMethods}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Rates'
        icon={<CircleDollarSignIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.rates}
        activeTabs={activeTabs}
      >
        <RatesForm
          formMethods={formMethods}
          showBuySellFields={true}
          isUpdateMode={true}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Pickup'
        icon={<BoxIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.pickup}
        activeTabs={activeTabs}
      >
        <StopForm
          formMethods={formMethods}
          stop={LoadStopTypes.Pickup}
          isUpdateMode={true}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Consignee'
        icon={<WarehouseIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.consignee}
        activeTabs={activeTabs}
      >
        <StopForm
          formMethods={formMethods}
          stop={LoadStopTypes.Consignee}
          isUpdateMode={true}
        />
      </LoadSectionAccordionItem>

      <LoadSectionAccordionItem
        label='Additional Refs'
        icon={<ReceiptIcon className='h-6 w-6' strokeWidth={1} />}
        name={AvailableTabs.additionalReferences}
        activeTabs={activeTabs}
      >
        <AdditionalReferencesForm formMethods={formMethods} />
      </LoadSectionAccordionItem>
    </Accordion>
  );

  const buttonText = isCreateMode ? ButtonText.BuildLoad : ButtonText.UpdateTMS;

  return (
    <div className='mb-5'>
      <ExtendedFormProvider
        aiDefaultValues={isCreateMode}
        highlightDirtyFields={!isCreateMode}
      >
        <FormProvider {...formMethods}>
          <form
            onSubmit={formMethods.handleSubmit(handleSubmit, handleInvalid)}
          >
            <LoadBuildingAccordionControls
              activeTabs={activeTabs}
              allTabs={allTabs}
              onExpandAll={expandAll}
              onCollapseAll={collapseAll}
              defaultView={defaultView}
              onDefaultViewChange={setDefaultView}
            />
            {isCreateMode ? createModeContent : editModeContent}

            <section className='w-full mt-4'>
              <Button
                buttonNamePosthog={
                  isCreateMode
                    ? ButtonNamePosthog.BuildLoad
                    : ButtonNamePosthog.UpdateTMS
                }
                type='submit'
                className='w-full'
                disabled={isLoading}
                data-testid={
                  isCreateMode
                    ? 'create-load-submit'
                    : 'load-info-update-submit'
                }
              >
                {isLoading ? <ButtonLoader /> : buttonText}
              </Button>

              {isCreateMode && formMethods.formState.isDirty && (
                <Flex justify='center' align='center'>
                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ClearForm}
                    type='button'
                    className='w-50% mt-4 h-8 text-sm text-neutral-500'
                    disabled={isLoading}
                    variant='outline'
                    onClick={handleClearForm}
                  >
                    {ButtonText.ClearForm}
                  </Button>
                </Flex>
              )}

              {isCreateMode && builtLoad?.externalTMSID && (
                <LoadCreatedMessage builtLoadId={builtLoad.externalTMSID} />
              )}
            </section>
          </form>
        </FormProvider>
      </ExtendedFormProvider>
    </div>
  );
}
